import { useCallback, useEffect, useRef, useState } from 'react'
import type { TranscriptBlock } from '../transport/api'
import { selectionHeld } from './selectionHold'

/**
 * How long a selection that has STOPPED CHANGING may keep the conversation still.
 *
 * The hold exists for the seconds a reader spends dragging the handles and pressing
 * Copy. Every drag fires `selectionchange` and restarts this, so it never cuts a
 * selection in progress; it only ends a hold nobody is using any more. One shot,
 * armed by the selection event itself — nothing runs while nothing is selected.
 */
const HOLD_IDLE_MS = 15_000

/**
 * HOLD THE CONVERSATION STILL WHILE IT IS BEING READ — the selection latch, its
 * queue, and its release paths (M8 cut 5, 2026-09-03).
 *
 * This was two TranscriptView refs (`holding`, `queued`) plus a document-level
 * `selectionchange` effect, reached into from the window engine's `apply`,
 * `backfill`, and agent-switch reset. Moving them here gives the latch a name and
 * a contract: the window reads `holding` and writes `queued` through the returned
 * refs, and calls `resetHold` when the conversation switches. Every comment below
 * came with its ref, verbatim.
 *
 * THE HOLD IS BOUNDED, AND IT SAYS SO — owner, 2026-10-04: 經常會有 Clean View 跟不上
 * Terminal 的內容, 然後 Clean View 的 Message 停止在某一個時間點不再更新了 … 修了好多次
 * 都修不好. Every earlier fix went into the wire; this latch was never on the list, and
 * it had exactly one release: the selection going away. On a phone it does not go away —
 * long-press › Copy leaves the highlight standing until something else is tapped, and it
 * scrolls off screen with the message. From then on every new block queued behind a hold
 * nobody could see: the footer's total kept counting ("7514 blocks · 1000 loaded") while
 * the conversation stood at the minute of the copy. Reproduced on the real app
 * (scripts/e2e-cleanview-stall.mjs, `selection`): the machine held the reply, the page
 * never drew it.
 *
 * So the hold now ends three more ways, each bound to what it guards (the copy):
 *   - the Copy itself — its purpose is served the moment the text is on the clipboard;
 *   - HOLD_IDLE_MS after the selection last changed — a highlight left standing is not
 *     a selection in progress;
 *   - leaving the app and coming back.
 * and `held` is state, so the view can say "paused while selecting" for as long as
 * blocks are actually waiting.
 */
export function useSelectionHold(args: {
  log: { current: HTMLDivElement | null }
  setBlocks: (blocks: TranscriptBlock[]) => void
}) {
  const { log, setBlocks } = args
  /**
   * True while the reader has text selected inside the log.
   *
   * Copying out of a live conversation is a race against the poll: any re-render that
   * touches the selected text collapses the selection, and at 2s intervals a long
   * quote is not reliably survivable. So while a selection is held this view stops
   * mutating — arriving blocks queue up and land the moment it is released. Nothing
   * is dropped; the transcript is just allowed to hold still while it is being read.
   */
  const holding = useRef(false)
  const queued = useRef<TranscriptBlock[] | null>(null)
  /**
   * Blocks are WAITING behind a selection — for the render, so the footer can say the
   * view is paused. Set when something is actually parked, not when a selection begins:
   * a selection with nothing waiting changes nothing on screen, and a render at the
   * very moment someone starts selecting is the one thing this hook exists to avoid.
   */
  const [held, setHeld] = useState(false)
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null)

  const disarm = () => {
    if (idle.current !== null) clearTimeout(idle.current)
    idle.current = null
  }

  /** End the hold, whatever started it, and show everything that waited behind it. */
  const release = useCallback(() => {
    disarm()
    if (!holding.current && !queued.current) return
    holding.current = false
    setHeld(false)
    if (queued.current) {
      const next = queued.current
      queued.current = null
      setBlocks(next)
    }
  }, [setBlocks])

  /** Keep `next` back until the hold ends — the window's `apply` while `holding`. */
  const park = useCallback((next: TranscriptBlock[]) => {
    queued.current = next
    setHeld(true)
  }, [])

  /*
   * Switching agents with a selection held would queue the NEW conversation behind
   * a hold belonging to the old one — the same silent freeze, reached a third way.
   * The selection cannot survive the DOM it was in, so the hold must not either.
   */
  const resetHold = useCallback(() => {
    disarm()
    holding.current = false
    queued.current = null
    setHeld(false)
  }, [])

  /**
   * Track whether a selection is live inside this log.
   *
   * `selectionchange` on the document rather than a handler on the element: a
   * selection can be extended or dropped by the caret handles, a keyboard, or the
   * system's own copy callout, and only the document-level event sees all of them.
   * Anchored inside the log, because selecting in the composer must not freeze the
   * conversation.
   */
  useEffect(() => {
    const live = () => selectionHeld(log.current, document.getSelection())
    const onSel = () => {
      if (!live()) {
        release()
        return
      }
      // The selection moved: it is in use. (Re)start the hold and its idle bound.
      holding.current = true
      disarm()
      idle.current = setTimeout(release, HOLD_IDLE_MS)
    }
    /*
     * COPIED: the hold has done its job. Released one task later, so the browser's own
     * copy — which reads the selection synchronously inside this event — is never the
     * thing that loses the race it was protecting.
     */
    const onCopy = () => {
      if (holding.current) setTimeout(release, 0)
    }
    /*
     * Re-evaluate on foreground too, because a missed event must not make the hold
     * permanent.
     *
     * If iOS drops the selection while the app is backgrounded WITHOUT firing
     * `selectionchange`, `holding` stayed true forever: the conversation stops
     * updating, nothing on screen says why, and the reader has no selection left to
     * release. And a selection that DID survive the trip is not one in progress — the
     * person left and came back — so returning releases either way. (It only ever
     * releases: beginning a hold belongs to `selectionchange` alone, which is what
     * arms the idle bound.)
     *
     * avengents-app-builder measured `visibilitychange` firing in the Capacitor
     * WebView across two independent runs, so this is a hook that exists rather than
     * one that ought to.
     */
    const onVisible = () => {
      if (document.visibilityState === 'visible') release()
    }
    document.addEventListener('selectionchange', onSel)
    document.addEventListener('copy', onCopy)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      disarm()
      document.removeEventListener('selectionchange', onSel)
      document.removeEventListener('copy', onCopy)
      document.removeEventListener('visibilitychange', onVisible)
    }
    // `log` is a ref object and `release` a useCallback over a useState setter — both
    // stable by construction, so this listener still mounts exactly once. Listed, not
    // suppressed: the linter is told the truth rather than silenced.
  }, [log, release])

  return { holding, queued, resetHold, held, release, park }
}
